import axios from 'axios';
import React, { useState, useEffect, useRef } from 'react'
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import { baseUrl } from '../../Environments/environment';
import { useParams } from 'react-router-dom';

// Skeleton Loading Component for Detail Modal
const DetailSkeleton = () => (
  <div className="fixed inset-0 z-50 overflow-hidden">
    {/* Backdrop */}
    <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" />
    
    {/* Bottom Sheet */}
    <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl transform transition-transform duration-300 ease-out max-h-[90vh] overflow-hidden">
      {/* Header Skeleton */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="w-9 h-9 bg-gray-200 rounded-full animate-pulse"></div>
        <div className="h-6 bg-gray-200 rounded w-32 animate-pulse"></div>
        <div className="w-9" />
      </div>

      {/* Content Skeleton */}
      <div className="overflow-y-auto max-h-[calc(90vh-60px)]">
        {/* Image Skeleton */}
        <div className="relative">
          <div className="aspect-video bg-gray-200 animate-pulse"></div>
        </div>

        {/* Room Info Skeleton */}
        <div className="p-4 space-y-4">
          {/* Title and Price Skeleton */}
          <div className="flex justify-between items-start">
            <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
            <div className="h-8 bg-gray-200 rounded w-20 animate-pulse"></div>
          </div>

          {/* Description Skeleton */}
          <div className="space-y-2">
            <div className="h-6 bg-gray-200 rounded w-24 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
          </div>
        </div>

        {/* Action Buttons Skeleton */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 space-y-3">
          <div className="h-12 bg-gray-200 rounded-lg animate-pulse"></div>
          <div className="h-12 bg-gray-200 rounded-lg animate-pulse"></div>
        </div>
      </div>
    </div>
  </div>
);

const UpgradeRoomDetail = ({ isOpen, onClose, room, onRoomSelect, isSubmitting = false, hasActiveUpgradeRequest = false, activeRequest = null }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isAutoScroll, setIsAutoScroll] = useState(true)
  const [submitLoading,setSubmitLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const intervalRef = useRef(null)
  const params  = useParams();
  const guestSessionId = params.id

  
  const images = room?.images || [room?.image] || []

  // Helper function to check if this room is the same as the active request
  const isRoomAlreadyRequested = (room) => {
    return activeRequest && activeRequest.roomType === room.id;
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length)
  }

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)
  }

  const goToImage = (index) => {
    setCurrentImageIndex(index)
  }

  // Pause auto-scroll when user interacts
  const handleUserInteraction = () => {
    setIsAutoScroll(false)
    // Resume auto-scroll after 5 seconds of no interaction
    setTimeout(() => {
      setIsAutoScroll(true)
    }, 5000)
  }

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll
      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'
    } else {
      // Restore body scroll
      document.body.style.overflow = 'unset'
      document.body.style.position = 'unset'
      document.body.style.width = 'unset'
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset'
      document.body.style.position = 'unset'
      document.body.style.width = 'unset'
    }
  }, [isOpen])

  // Loading and auto-scroll functionality
  useEffect(() => {
    if (isOpen && room) {
      // Simulate loading delay for detail modal
      setIsLoading(true)
      const loadDetail = async () => {
        await new Promise(resolve => setTimeout(resolve, 800))
        setIsLoading(false)
      }
      loadDetail()
    }
  }, [isOpen, room])

  useEffect(() => {
    if (isOpen && images.length > 1 && isAutoScroll && !isLoading) {
      intervalRef.current = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length)
      }, 3000) // Change image every 3 seconds
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [isOpen, images.length, isAutoScroll, isLoading])
  
  if (!isOpen || !room) return null

  // Show skeleton while loading
  if (isLoading) {
    return <DetailSkeleton />
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Bottom Sheet */}
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl transform transition-transform duration-300 ease-out max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <FiX className="w-5 h-5 text-gray-600" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900">Room Details</h2>
          <div className="w-9" /> {/* Spacer for centering */}
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-60px)]">
          {/* Image Gallery */}
          <div className="relative">
            <div className="aspect-video bg-gray-200">
              {images.length > 0 ? (
                <img
                  src={images[currentImageIndex]}
                  alt={`${room.roomType} - Image ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <span>No Image Available</span>
                </div>
              )}
            </div>
            
            {/* Image Navigation */}
            {images.length > 1 && (
              <>
                <button
                  onClick={() => {
                    prevImage()
                    handleUserInteraction()
                  }}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
                >
                  <FiChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    nextImage()
                    handleUserInteraction()
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
                >
                  <FiChevronRight className="w-5 h-5" />
                </button>
                
                {/* Image Indicators */}
                <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
                  {images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        goToImage(index)
                        handleUserInteraction()
                      }}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentImageIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                      }`}
                    />
                  ))}
                </div>

                {/* Auto-scroll indicator */}
                <div className="absolute top-2 right-2">
                  <div className={`w-3 h-3 rounded-full transition-all ${
                    isAutoScroll ? 'bg-green-500' : 'bg-gray-400'
                  }`} title={isAutoScroll ? 'Auto-scroll active' : 'Auto-scroll paused'} />
                </div>
              </>
            )}
          </div>

          {/* Room Info */}
          <div className="p-4 space-y-4">
            {/* Title and Price */}
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{room.roomType}</h1>
              </div>
              <div className="text-right">
                {room.price && (
                  <div className="text-2xl font-bold text-indigo-600">
                    ${room.price}
                    <span className="text-sm font-normal text-gray-500">/night</span>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            {room.description && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
                <p className="text-gray-600 leading-relaxed">{room.description}</p>
              </div>
            )}

          </div>

          {/* Action Buttons */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 space-y-3">
            <button
              onClick={() => {
                if (onRoomSelect && !hasActiveUpgradeRequest) {
                  onRoomSelect(room)
                } else if (!hasActiveUpgradeRequest) {
                  console.log('Selected room:', room)
                  onClose()
                }
              }}
              disabled={isSubmitting || hasActiveUpgradeRequest}
              className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                isSubmitting || hasActiveUpgradeRequest
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Submitting Request...
                </div>
              ) : isRoomAlreadyRequested(room) ? (
                'Request Already Sent'
              ) : hasActiveUpgradeRequest ? (
                'Select Room'
              ) : (
                'Select This Room'
              )}
            </button>
            <button
              onClick={onClose}
              className="w-full border border-gray-300 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UpgradeRoomDetail
